import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { AcModel } from 'projects/core/src/lib/models/ac.model';
import { AppointmentModel } from 'projects/core/src/lib/models/appointment.model';
import { MoveDataPayload, MoveOrderDataComponent } from './partials/move-order-data/move-order-data.component';
import { LocationModel } from 'projects/core/src/lib/models/location.model';
import { OrderDetailParticipantsComponent } from '../order-detail/partials/order-detail-participants/order-detail-participants.component';
import { SelectedSuggestion } from './partials/move-order-suggestions/move-order-suggestions.types';
import moment from 'moment';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { TranslocoService } from '@ngneat/transloco';

const ROLES = ['moderator', 'moderator_external', 'observer', 'observer_gem'];

export type UserAppointments = { [key: number]: Array<AppointmentModel> } | null;
export type UserLocations = { [key: number]: Array<number> } | null;

export interface MovingData {
  appointments: UserAppointments;
  user_locations: UserLocations;
  locations: Array<LocationModel>;
  settings: { [key: string]: any } | null;
}

export interface MoveOrderPayload {
  data: any;
  participants: any;
}

enum FormMode {
  Edit = 'edit',
  Success = 'success',
}

@Component({
  selector: 'app-move-order',
  templateUrl: './move-order.component.html',
  styleUrls: ['./move-order.component.scss']
})
export class MoveOrderComponent implements OnInit {

  @Input() order: AcModel = null;
  @Input() options: { [key: string]: Array<any> };
  @Output() closed = new EventEmitter<void>();
  @Output() submitted = new EventEmitter<AcModel>();

  @ViewChild('data') data: MoveOrderDataComponent;
  @ViewChild('participants') participants: OrderDetailParticipantsComponent;

  public loading = false;
  public submitting = false;

  public form: UntypedFormGroup = this.fb.group({});
  public movingData: MovingData | null = null;
  public movingDate: Date | null = null;
  public movingLocation: LocationModel | null = null;
  private originalParticipants: string | null = null;
  private originalLocationData: string | null = null;

  public mode: FormMode = FormMode.Edit;
  public FormMode = FormMode;
  public suggestionsVisible = false;

  constructor(
    private api: ApiService,
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private transloco: TranslocoService,
  ) { }

  ngOnInit(): void {
    this.loadData();
  }

  public onDataChanged(payload: MoveDataPayload): void {
    this.movingDate = payload.date;
    this.movingLocation = payload.location;
  }

  public onCancel(): void {
    this.closed.emit();
  }

  public onSubmit(): void {
    const dataPayload = this.data.getPayload();
    const participantsPayload = this.participants.getPayload();
    if (dataPayload !== null && participantsPayload !== null) {
      this.ui.hideMessage();
      this.submitting = true;
      const payload: MoveOrderPayload = {
        data: dataPayload,
        participants: participantsPayload.users?.filter((user: any) => {
          return ROLES.includes(user.role);
        }),
      };
      this.api.moveOrder(this.order.id, payload).subscribe({
        next: (result: any) => {
          if (result.data) {
            this.order = new AcModel().deserialize(result.data);
            this.mode = FormMode.Success;
            this.submitted.emit(this.order);
            this.closed.emit();
          }
        },
        error: (error: any) => {
          this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
        },
      }).add(() => {
        this.submitting = false;
      });
    }
  }

  public isSendDisabled(): boolean {
    const convertedMovingDate = moment.tz(moment(this.movingDate).format('YYYY-MM-DD HH:mm:ss'), 'Europe/Berlin');
    const sameDate = this.movingDate === null || convertedMovingDate.isSame(this.order?.start_time, 'minute');
    const sameLocation = this.isSameLocation();
    const participantsChanged = this.haveParticipantsChanged();
    const isBlocked = this.participants?.isBlocked();
    const notEnoughParticipants = (this.participants?.hasEnoughParticipants() || false) === false;
    
    // Button should be enabled if:
    // - Date or Location changed OR Participants changed
    // - AND not blocked AND has enough participants
    const noChanges = sameDate && sameLocation && !participantsChanged;
    return noChanges || isBlocked || notEnoughParticipants;
  }

  public onToggleSuggestions(): void {
    this.suggestionsVisible = !this.suggestionsVisible;
  }

  public onSuggestionSelected(payload: SelectedSuggestion): void {
    this.data.setSuggestion(payload);
    this.participants.setSuggestion(payload);
    this.suggestionsVisible = false;
  }

  public sendInvitation(): void {}

  private storeOriginalParticipants(): void {
    if (this.participants) {
      const payload = this.participants.getPayload();
      this.originalParticipants = JSON.stringify(payload);
    }
  }

  private storeOriginalLocationData(): void {
    if (this.data) {
      const payload = this.data.getPayload();
      if (payload !== null) {
        // Store only location-relevant data for comparison
        const locationData = {
          location_id: payload.values.location,
          room1_id: payload.values.room1,
          room2_id: payload.values.room2,
          room_main: payload.values.room_main,
          room_preparation: payload.values.room_preparation,
        };
        this.originalLocationData = JSON.stringify(locationData);
      }
    }
  }

  private isSameLocation(): boolean {
    // If movingLocation is null, location hasn't changed
    if (this.movingLocation === null) {
      // But we still need to check if rooms have changed
      return this.areRoomsSame();
    }
    
    // If movingLocation is set but different from original, location has changed
    if (this.movingLocation?.id !== this.order?.location_id) {
      return false;
    }
    
    // Location ID is the same, but check if rooms have changed
    return this.areRoomsSame();
  }

  private areRoomsSame(): boolean {
    if (!this.data || this.originalLocationData === null) {
      return true;
    }
    
    const currentPayload = this.data.getPayload();
    if (currentPayload === null) {
      return true;
    }
    
    const currentLocationData = {
      location_id: currentPayload.values.location,
      room1_id: currentPayload.values.room1,
      room2_id: currentPayload.values.room2,
      room_main: currentPayload.values.room_main,
      room_preparation: currentPayload.values.room_preparation,
    };
    
    return this.originalLocationData === JSON.stringify(currentLocationData);
  }

  private haveParticipantsChanged(): boolean {
    if (!this.participants || this.originalParticipants === null) {
      return false;
    }
    const currentPayload = this.participants.getPayload();
    const currentParticipantsString = JSON.stringify(currentPayload);
    return this.originalParticipants !== currentParticipantsString;
  }

  private loadData(): void {
    this.loading = true;
    this.api.getOrderMovingData(this.order.id).subscribe({
      next: (result: any) => {
        const appointments = result.data?.appointments?.map((data: any) => {
          return new AppointmentModel().deserialize(data);
        }) || [];
        let grouped: UserAppointments | null = null;
        if (appointments.length > 0) {
          grouped = appointments.reduce((acc: UserAppointments, appointment: AppointmentModel) => {
            acc[appointment.owner_id] = [...(acc[appointment.owner_id] || []), appointment];
            return acc;
          }, {});
        }
        this.movingData = {
          appointments: grouped,
          locations: result.data?.locations.map(location => new LocationModel().deserialize(location)) || [],
          user_locations: result.data?.user_locations || [],
          settings: result.data?.settings || null,
        };
        // Store original state after data is loaded and components are initialized
        setTimeout(() => {
          this.storeOriginalParticipants();
          this.storeOriginalLocationData();
        }, 100);
      },
      error: (error: any) => {

      },
    }).add(() => {
      this.loading = false;
    });
  }

}
